import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { assetThumb, listAssets, loadMentionAssets, mentionListComplete, peekMentionAssets, type Asset, type AssetKind } from '../../../api/assets'
import type { MentionQuery } from '../../../prompt-vars/PromptPillEditor'

/**
 * Меню «@» в промпте (Ф2 «Библиотека сцены»): свои и библиотечные ассеты вперемешку, как у Artlist —
 * строка 32 px, аватар 16 px, тип справа. Выбор вставляет чип-упоминание, ассет сам встаёт в сцену
 * (useSceneApply видит новое упоминание). Клавиши ведёт хост через onMentionKey редактора.
 */

const KIND_LABEL: Record<AssetKind, string> = { character: 'Аватар', location: 'Локация', prop: 'Предмет' }

function KindIcon({ kind }: { kind: AssetKind }) {
	if (kind === 'location')
		return (
			<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
				<path d="M3 19 9.5 9l4 6 2.5-3.5L21 19Z" /><circle cx="16.5" cy="6.5" r="1.8" />
			</svg>
		)
	if (kind === 'prop')
		return (
			<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
				<path d="M12 3 20 7.5v9L12 21l-8-4.5v-9Z" /><path d="M4 7.5 12 12l8-4.5M12 12v9" />
			</svg>
		)
	return (
		<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
			<circle cx="12" cy="8" r="3.4" /><path d="M5 20c1.3-3.4 3.9-5 7-5s5.7 1.6 7 5" />
		</svg>
	)
}

/** Миниатюра строки: превью; не открылось — оригинал; нет и его — пустой кружок (а не значок битой картинки). */
function Ava({ asset }: { asset: Asset }) {
	const [src, setSrc] = useState<string | null>(assetThumb(asset))
	if (!src) return <span className="scene-mention__ava" aria-hidden="true" />
	return (
		<img className="scene-mention__ava" src={src} alt="" loading="lazy"
			onError={() => setSrc(asset.avatar_url && asset.avatar_url !== src ? asset.avatar_url : null)} />
	)
}

export interface SceneMentionHandle {
	/** Клавиша от редактора: true — обработана. */
	key: (k: string) => boolean
}

export default function SceneMentionMenu({
	query,
	onPick,
	onClose,
	handleRef,
	onAssets,
	dark = false,
}: {
	query: MentionQuery
	onPick: (a: Asset) => void
	onClose: () => void
	handleRef: React.MutableRefObject<SceneMentionHandle | null>
	/** Ассеты подгружены (общий список или найденное поиском) — хосту для подписей чипов: дополнять, не заменять. */
	onAssets?: (list: Asset[]) => void
	/** Меню над тёмной поверхностью при любой теме сайта (страница «Сцена»). */
	dark?: boolean
}) {
	const [all, setAll] = useState<Asset[] | null>(peekMentionAssets())
	const [failed, setFailed] = useState(false)
	const [remote, setRemote] = useState<{ q: string; items: Asset[] } | null>(null)
	const [active, setActive] = useState(0)
	const listRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		let alive = true
		loadMentionAssets()
			.then((list) => {
				if (!alive) return
				setAll(list)
				onAssets?.(list)
			})
			// Список не загрузился: прежний (из кэша) остаётся, а если его нет — говорим об ошибке, а не «у вас пусто».
			.catch(() => { if (alive) setFailed(true) })
		return () => { alive = false }
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const q = query.query.trim().toLowerCase()
	const local = useMemo(() => (all ?? []).filter((a) => !q || a.name.toLowerCase().includes(q)), [all, q])

	// В кэше — первые страницы своих и библиотеки. Если у сервера больше, ищем по названию и там: свои (готовые)
	// и библиотечные; иначе до ассетов за пределами первой страницы через «@» было не добраться.
	useEffect(() => {
		if (!q || all === null || mentionListComplete()) return
		let alive = true
		const t = setTimeout(() => {
			Promise.all([listAssets({ scope: 'mine', q, pageSize: 20 }), listAssets({ scope: 'library', q, pageSize: 20 })])
				.then(([m, l]) => {
					if (!alive) return
					const items = [...m.items.filter((a) => a.status === 'ready'), ...l.items]
					setRemote({ q, items })
					onAssets?.(items)
				})
				.catch(() => { if (alive) setRemote({ q, items: [] }) })
		}, 250)
		return () => { alive = false; clearTimeout(t) }
	}, [q, all]) // eslint-disable-line react-hooks/exhaustive-deps

	const items = useMemo(() => {
		const seen = new Set(local.map((a) => a.id))
		const extra = remote && remote.q === q ? remote.items.filter((a) => !seen.has(a.id)) : []
		return [...local, ...extra].slice(0, 30)
	}, [local, remote, q])
	const idx = Math.min(active, Math.max(0, items.length - 1))

	useEffect(() => {
		handleRef.current = {
			key: (k: string) => {
				if (k === 'Escape') { onClose(); return true }
				if (!items.length) return false
				if (k === 'ArrowDown') { setActive((i) => (Math.min(i, items.length - 1) + 1) % items.length); return true }
				if (k === 'ArrowUp') { setActive((i) => (Math.min(i, items.length - 1) - 1 + items.length) % items.length); return true }
				if (k === 'Enter' || k === 'Tab') { onPick(items[idx]); return true }
				return false
			},
		}
		return () => { handleRef.current = null }
	})

	useEffect(() => {
		const row = listRef.current?.children[idx] as HTMLElement | undefined
		row?.scrollIntoView({ block: 'nearest' })
	}, [idx])

	// Под строкой каретки; не влезает снизу — над ней. Считаем по видимой части экрана: на телефоне клавиатура
	// закрывает низ окна, и меню «под кареткой» уезжало бы под неё.
	const vv = window.visualViewport
	const viewTop = vv?.offsetTop ?? 0
	const viewBottom = viewTop + (vv?.height ?? window.innerHeight)
	const roomBelow = viewBottom - query.bottom - 12
	const roomAbove = query.top - viewTop - 12
	const below = roomBelow >= 300 || roomBelow >= roomAbove
	const left = Math.max(8, Math.min(query.left - 8, window.innerWidth - 272))
	const maxHeight = Math.max(96, Math.min(288, below ? roomBelow : roomAbove))
	const style = below ? { left, top: query.bottom + 6, maxHeight } : { left, bottom: window.innerHeight - query.top + 6, maxHeight }

	return createPortal(
		<div className={`scene-mention${dark ? ' scene-mention--dark' : ''}`} style={style} role="listbox" aria-label="Аватары, локации и предметы" ref={listRef}
			onMouseDown={(e) => e.preventDefault()}>
			{all === null && !failed && <div className="scene-mention__empty">Загружаем…</div>}
			{all === null && failed && <div className="scene-mention__empty">Список не загрузился — проверьте связь и наберите «@» ещё раз</div>}
			{all !== null && !items.length && (
				<div className="scene-mention__empty">
					{q ? `Ничего не нашли по «${query.query.trim()}»` : 'Здесь будут ваши аватары, локации и предметы — добавьте их в меню фото-слота'}
				</div>
			)}
			{items.map((a, i) => (
				<button
					key={a.id}
					type="button"
					role="option"
					aria-selected={i === idx}
					className={`scene-mention__item${i === idx ? ' is-active' : ''}`}
					onMouseEnter={() => setActive(i)}
					onClick={() => onPick(a)}
					title={`${KIND_LABEL[a.kind]}${a.is_stock ? ' · из библиотеки' : ''}`}
				>
					<Ava asset={a} />
					<span className="scene-mention__name">{a.name}</span>
					<span className="scene-mention__kind"><KindIcon kind={a.kind} /></span>
				</button>
			))}
		</div>,
		document.body,
	)
}
